import React, { useState } from 'react';
import _ from 'lodash';
import PanelEntry from './PanelEntry';
import { Button, Icon, TextField, Typography } from '@scale/scaleui';
import { OnboardingFlowNodeProjectDistribution } from '@scale/shared/onboardingControlPanel';

export const MAX_WEIGHT = 100;

interface ProjectDistributionConfigProps {
  chosenProjectDistribution: OnboardingFlowNodeProjectDistribution[];
  setChosenProjectDistribution: React.Dispatch<
    React.SetStateAction<OnboardingFlowNodeProjectDistribution[]>
  >;
}

export const ProjectDistributionConfig: React.FC<ProjectDistributionConfigProps> = ({
  chosenProjectDistribution,
  setChosenProjectDistribution,
}) => {
  const [tempProjectDistribution, setTempProjectDistribution] =
    useState<OnboardingFlowNodeProjectDistribution>({ projectId: '', distribution: 0 });

  const remainingWeight = React.useMemo(() => {
    const totalWeight: number = chosenProjectDistribution.reduce(
      (sum, project) => sum + project.distribution,
      0,
    );
    return MAX_WEIGHT - totalWeight;
  }, [chosenProjectDistribution]);

  const updateProjectDistribution = (
    e: React.ChangeEvent<HTMLInputElement>,
    projectId: string,
    distribution: number,
  ) =>
    setChosenProjectDistribution(prev =>
      prev.map(p => {
        if (p.projectId === projectId) {
          const newValue = parseInt(e.target.value || '0');
          const maxValue = remainingWeight + distribution;
          const newDistribution = Math.min(isNaN(newValue) ? 0 : newValue, maxValue);
          return { ...p, distribution: newDistribution };
        }
        return p;
      }),
    );
  const addTempProjectDistribution = () => {
    setChosenProjectDistribution(prev =>
      tempProjectDistribution.projectId
        ? _.unionBy(prev, [tempProjectDistribution], 'projectId')
        : prev,
    );
    setTempProjectDistribution({ projectId: '', distribution: 0 });
  };
  const addTempProjectId = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTempProjectDistribution(prev => ({ ...prev, projectId: e.target.value }));
  const addTempDistribution = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTempProjectDistribution(prev => {
      const newValue = parseInt(e.target.value || '0');
      const newDistribution = Math.min(isNaN(newValue) ? 0 : newValue, remainingWeight);
      return { ...prev, distribution: newDistribution };
    });
  const removeProjectDistribution = (projectId: string) =>
    setChosenProjectDistribution(prev => prev.filter(p => p.projectId !== projectId));

  return (
    <PanelEntry title="Project Distribution">
      {chosenProjectDistribution &&
        chosenProjectDistribution.map(({ projectId, distribution }) => (
          <div key={projectId} className="flex gap-2 justify-between items-center">
            <Typography.Paragraph2>{projectId}</Typography.Paragraph2>
            <div key={projectId} className="flex gap-2 items-center">
              <TextField
                id="Percent"
                value={distribution}
                placeholder="%"
                onChange={e => updateProjectDistribution(e, projectId, distribution)}
              />
              <Typography.Paragraph2>%</Typography.Paragraph2>
              <Icon
                rounded
                size="sm"
                icon="minus"
                variant="neutral"
                onClick={() => removeProjectDistribution(projectId)}
              />
            </div>
          </div>
        ))}
      <div className="flex gap-2 items-center">
        <TextField
          id="Project ID"
          value={tempProjectDistribution.projectId}
          placeholder="Project ID"
          onChange={addTempProjectId}
        />
        <TextField
          id="Percent"
          value={tempProjectDistribution.distribution}
          placeholder="%"
          onChange={addTempDistribution}
        />
        <Typography.Paragraph2>%</Typography.Paragraph2>
        <Button
          disabled={!tempProjectDistribution.projectId || !tempProjectDistribution.distribution}
          onClick={addTempProjectDistribution}
        >
          Add
        </Button>
      </div>
    </PanelEntry>
  );
};
